import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Row,
  Col,
  Segmented,
  Select,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import dayjs from "dayjs";
import { useWarehouses } from "@/hooks/useWarehouses";
import { useGeneralConfig } from "@/hooks/useGeneralConfig";
import {
  orderImportService,
  type OrderImportFulfillmentType,
  type OrderImportOrderType,
  type OrderImportStatus,
} from "@/services/order-import.service";
import { downloadBlob } from "@/utils/downloadBlob";
import { getApiErrorMessage } from "@/utils/apiError";
import { OrderImportResultTable } from "./OrderImportResultTable";

const { Text } = Typography;

const IMPORT_POLL_INTERVAL_MS = 1500;
const IMPORT_POLL_MAX_ATTEMPTS = 80;
// TCR-1547: job kẹt ở "pending" (chưa từng sang "processing") quá ngưỡng này coi như
// dấu hiệu worker nền chưa nhặt job khỏi hàng đợi, khác với job đang chạy thật mà chậm.
const PENDING_STALL_MS = 20_000;

// I4: lý do khoá nút "Tạo đơn hàng" khi chưa biết chắc `pos_sales_modes` —
// nói thẳng bằng tiếng Việt thay vì âm thầm giả định Giao ngay.
const CONFIG_UNRESOLVED_ERROR =
  "Chưa đọc được cấu hình hình thức bán của cửa hàng — chưa thể tạo đơn từ file. Vui lòng tải lại trang rồi thử lại.";
const CONFIG_LOADING_MESSAGE =
  "Đang tải cấu hình hình thức bán của cửa hàng — vui lòng đợi trong giây lát.";

const FULFILLMENT_TYPE_OPTIONS: Array<{
  value: OrderImportFulfillmentType;
  label: string;
}> = [
  { value: "INSTANT", label: "Giao ngay" },
  { value: "PREORDER", label: "Đặt trước" },
];

const ORDER_TYPE_OPTIONS: Array<{
  value: OrderImportOrderType;
  label: string;
}> = [
  { value: "carrier", label: "Liên kết ĐVVC" },
  { value: "self_ship", label: "Tự vận chuyển" },
  { value: "in_store", label: "Bán tại cửa hàng" },
];

export interface ImportOrderModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Poll trạng thái job import cho tới khi `completed`/`failed`, hoặc hết `IMPORT_POLL_MAX_ATTEMPTS`
 * lượt (~2 phút — vẫn `pending`/`processing`), hoặc bị huỷ giữa chừng (`isCancelled()` trả `true`
 * khi modal đã đóng hoặc một lượt submit khác đã bắt đầu — xem `pollTokenRef` ở component).
 *
 * `onTick` được gọi sau MỖI lần `getStatus` thành công mà chưa bị huỷ, để caller cập nhật UI
 * tiến độ (TCR-1547) — trần 2 phút vẫn giữ nguyên, chỉ thêm chỗ để hiện người dùng biết chuyện
 * gì đang xảy ra trong lúc chờ thay vì màn hình trắng.
 *
 * Trả về:
 * - job cuối cùng nếu dừng ở `completed`/`failed`.
 * - job cuối cùng (còn `pending`/`processing`) nếu hết lượt poll mà chưa xong — KHÔNG coi đây
 *   là thành công, caller phải tự phân biệt qua `status`.
 * - `null` nếu bị huỷ — caller phải bỏ qua kết quả này (không `setResult`/`onImported`).
 */
async function waitForImportResult(
  importId: string,
  isCancelled: () => boolean,
  onTick: (job: OrderImportStatus) => void,
): Promise<OrderImportStatus | null> {
  let lastJob: OrderImportStatus | null = null;
  for (let attempt = 0; attempt < IMPORT_POLL_MAX_ATTEMPTS; attempt += 1) {
    if (isCancelled()) return null;
    lastJob = await orderImportService.getStatus(importId);
    if (isCancelled()) return null;
    onTick(lastJob);
    if (lastJob.status === "completed" || lastJob.status === "failed") {
      return lastJob;
    }
    await sleep(IMPORT_POLL_INTERVAL_MS);
  }
  return lastJob;
}

/**
 * M1 (review tổng cuối): Alert tổng kết trước đây đếm `warning_rows` — biến đó
 * nay VĨNH VIỄN rỗng (cơ chế cũ đã bị `order_results` thay), nên 10 đơn cảnh
 * báo vẫn hiện "0 dòng cảnh báo". Đếm từ `order_results`.
 *
 * Kèm theo I2: `error_rows` nay CHỈ còn lỗi cấp DÒNG không quy được về đơn nào
 * — lấy nó làm số "đơn lỗi" sẽ hiện 0 dù 5 đơn thất bại. Đơn lỗi cũng đếm từ
 * `order_results`; `error_rows` được gọi đúng tên của nó và chỉ nhắc tới khi
 * thật sự có.
 */
function summarize(job: OrderImportStatus): string {
  const results = job.order_results ?? [];
  const failedOrders = results.filter((item) => item.status === "failed").length;
  const warningOrders = results.filter(
    (item) => item.status === "warning",
  ).length;
  const orphanRows = (job.error_rows ?? []).length;
  const parts = [
    `Đã tạo ${job.success_orders} đơn hàng`,
    `${failedOrders} đơn lỗi`,
    `${warningOrders} đơn cảnh báo`,
  ];
  if (orphanRows > 0) {
    parts.push(`${orphanRows} dòng lỗi không thuộc đơn nào`);
  }
  return `${parts.join(", ")}.`;
}

export function ImportOrderModal({
  open,
  onClose,
  onImported,
}: ImportOrderModalProps) {
  // Cleanup #6 (review cuối TCR-1529): trước đây gọi vô điều kiện nên trang
  // /orders fetch danh sách kho mỗi lần render dù modal đang đóng. `enabled:
  // open` chỉ fetch khi modal thật sự mở.
  const { data: warehousesData, isLoading: isLoadingWarehouses } =
    useWarehouses({ state: "1", page_size: 100 }, { enabled: open });
  // Task 8: `pos_sales_modes` quyết định modal có cho chọn hình thức bán hay
  // không — khớp luật màn POS đang áp dụng (src/pages/POS/index.tsx, quanh
  // `canSellInstant`/`canSellPreorder`).
  //
  // I4 (review tổng cuối): `data` KHÔNG đủ. Docblock của chính
  // `src/hooks/useGeneralConfig.ts` bắt buộc kiểm `isResolved` (= `isSuccess`)
  // TRƯỚC mọi hành động có hậu quả. `GET settings` lỗi (sau khi đã retry) ⇒
  // `data === undefined` ⇒ `salesModes` rơi về `['INSTANT']` ⇒ cụm chọn hình
  // thức bán không bao giờ hiện và effect ép `fulfillmentType = 'INSTANT'`:
  // tenant bật cả hai chế độ, người dùng muốn nhập đơn Đặt trước lại tạo 50
  // đơn INSTANT GIỮ TỒN THẬT mà không một dấu hiệu nào. Fallback `['INSTANT']`
  // vì thế CHỈ dùng để render, còn nút tạo đơn bị khoá tới khi biết chắc.
  const {
    data: generalConfig,
    isSuccess: isConfigResolved,
    isLoading: isConfigLoading,
  } = useGeneralConfig();
  const salesModes = generalConfig?.pos_sales_modes ?? ["INSTANT"];
  const canInstant = salesModes.includes("INSTANT");
  const canPreorder = salesModes.includes("PREORDER");
  // Chỉ một chế độ được bật ⇒ không có gì để chọn: ẩn cụm và ngầm định chế độ
  // đó (INSTANT nếu chỉ INSTANT, PREORDER nếu chỉ PREORDER) — đúng luật màn
  // POS đang áp dụng.
  const showModePicker = canInstant && canPreorder;
  const [orderType, setOrderType] = useState<OrderImportOrderType>("carrier");
  const [warehouseId, setWarehouseId] = useState<string | undefined>(undefined);
  const [fulfillmentType, setFulfillmentType] =
    useState<OrderImportFulfillmentType>("INSTANT");
  const [deliveryDays, setDeliveryDays] = useState<number | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OrderImportStatus | null>(null);
  // TCR-1547: id phiên import hiện tại, hiện kèm thông báo tiến độ để người dùng có
  // mã tra cứu/báo lỗi nếu cần, và để `handleRecheck` biết gọi `getStatus` cho job nào.
  const [importId, setImportId] = useState<string | null>(null);
  // TCR-1547 + Task 9: tiến độ poll — cập nhật qua `onTick` của `waitForImportResult`,
  // thay cho spinner câm nín suốt tối đa 2 phút. Lưu NGUYÊN job của tick (không chỉ vài
  // con số tóm tắt) để `OrderImportResultTable` render dần bảng kết quả từng đơn ngay
  // trong lúc job đang chạy, thay vì chỉ có thanh tiến độ.
  const [tick, setTick] = useState<{
    job: OrderImportStatus;
    elapsedMs: number;
  } | null>(null);
  // TCR-1547: true khi đã hết `IMPORT_POLL_MAX_ATTEMPTS` lượt mà job vẫn chưa xong —
  // hiện Alert cảnh báo + nút "Kiểm tra lại" thay vì im lặng.
  const [pollExhausted, setPollExhausted] = useState(false);
  // TCR-1547: đang gọi lại `getStatus` thủ công từ nút "Kiểm tra lại".
  const [rechecking, setRechecking] = useState(false);
  // Tăng mỗi lần modal đóng hoặc một lượt submit mới bắt đầu, để vòng poll của lượt submit
  // trước đó (nếu còn đang chạy) tự nhận ra mình đã "cũ" và bỏ qua kết quả — tránh 2 vòng
  // poll song song ghi đè kết quả của nhau khi người dùng đóng modal rồi import file khác.
  const pollTokenRef = useRef(0);

  useEffect(() => {
    if (!open) {
      pollTokenRef.current += 1;
      setOrderType("carrier");
      setWarehouseId(undefined);
      setFileList([]);
      setSubmitting(false);
      setResult(null);
      setImportId(null);
      setTick(null);
      setPollExhausted(false);
      setRechecking(false);
      setFulfillmentType(canInstant ? "INSTANT" : "PREORDER");
      setDeliveryDays(null);
    }
  }, [open, canInstant]);

  // Task 8: khi tenant chỉ bật một chế độ, `fulfillmentType` phải luôn khớp
  // chế độ đó (không có picker để người dùng tự chọn) — kể cả lúc modal đang
  // mở nếu setting đổi giữa chừng (tải chậm hơn lần render đầu).
  // I4: chỉ ép khi ĐÃ biết chắc cấu hình — chưa resolve thì `canInstant` là
  // giá trị của fallback, không phải sự thật của tenant.
  useEffect(() => {
    if (isConfigResolved && !showModePicker) {
      setFulfillmentType(canInstant ? "INSTANT" : "PREORDER");
    }
  }, [canInstant, showModePicker, isConfigResolved]);

  const warehouseOptions = (warehousesData?.data ?? []).map((warehouse) => ({
    value: warehouse._id ?? "",
    label: `${warehouse.name} — ${warehouse.store_name ?? "Toàn hệ thống"}`,
  }));

  async function handleDownloadTemplate() {
    try {
      const blob = await orderImportService.downloadTemplate();
      downloadBlob(blob, "mau-nhap-don-hang.xlsx");
    } catch (error) {
      message.error(
        getApiErrorMessage(error, "Không tải được file mẫu, vui lòng thử lại."),
      );
    }
  }

  async function handleSubmit() {
    // I4: chốt cứng — chưa biết chắc `pos_sales_modes` thì KHÔNG tạo đơn.
    // Nút đã bị `disabled`, đây là lớp thứ hai cho mọi đường vào khác.
    if (!isConfigResolved) {
      message.error(CONFIG_UNRESOLVED_ERROR);
      return;
    }
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.error("Vui lòng chọn file để tải lên.");
      return;
    }
    if (!warehouseId) {
      message.error("Vui lòng chọn kho trừ tồn.");
      return;
    }
    if (fulfillmentType === "PREORDER" && (!deliveryDays || deliveryDays < 1)) {
      message.error("Vui lòng nhập số ngày giao dự kiến.");
      return;
    }

    pollTokenRef.current += 1;
    const token = pollTokenRef.current;
    const isCancelled = () => pollTokenRef.current !== token;

    setSubmitting(true);
    setResult(null);
    setImportId(null);
    setTick(null);
    setPollExhausted(false);
    try {
      const { import_id } = await orderImportService.start({
        file,
        order_type: orderType,
        warehouse_id: warehouseId,
        fulfillment_type: fulfillmentType,
        delivery_days:
          fulfillmentType === "PREORDER" ? (deliveryDays ?? undefined) : undefined,
      });
      if (isCancelled()) return;
      setImportId(import_id);
      // Job giả lập lúc chưa có lượt getStatus nào — chỉ để `OrderImportResultTable`
      // có gì đó để render (bảng rỗng) thay vì phải chờ tick đầu tiên.
      setTick({
        job: {
          import_id,
          status: "pending",
          total_rows: 0,
          total_orders: 0,
          success_orders: 0,
          error_rows: [],
          warning_rows: [],
          error_message: null,
          fulfillment_type: fulfillmentType,
          delivery_days: fulfillmentType === "PREORDER" ? deliveryDays : null,
          processed_orders: 0,
          order_results: [],
        },
        elapsedMs: 0,
      });
      const startedAt = Date.now();
      const job = await waitForImportResult(import_id, isCancelled, (tickJob) => {
        if (!isCancelled()) {
          setTick({ job: tickJob, elapsedMs: Date.now() - startedAt });
        }
      });
      if (isCancelled() || !job) return;
      setResult(job);
      // Hết lượt poll (~2 phút) mà job vẫn chưa xong: đánh dấu để hiện Alert cảnh báo +
      // nút "Kiểm tra lại" thay vì im lặng (TCR-1547).
      if (job.status !== "completed" && job.status !== "failed") {
        setPollExhausted(true);
      }
      // Chỉ báo cho trang cha refetch khi job THẬT SỰ xong (completed) — cùng nguyên tắc với
      // `isSuccessfulImportJob` trong ExcelImportWizard. `failed` hoặc còn pending/processing
      // (hết lượt poll) chưa chắc chắn đã có đơn nào được tạo.
      if (job.status === "completed") onImported();
    } catch (error) {
      if (!isCancelled()) {
        message.error(
          getApiErrorMessage(error, "Nhập đơn hàng từ file Excel thất bại."),
        );
      }
    } finally {
      if (!isCancelled()) setSubmitting(false);
    }
  }

  /**
   * TCR-1547: nút "Kiểm tra lại" khi đã hết lượt poll tự động (2 phút) mà job vẫn
   * pending/processing — gọi thủ công 1 lần `getStatus` thay vì bắt người dùng chờ
   * thêm hoặc đoán mò. Đơn có thể vẫn đang được tạo ở nền (worker chậm), nên KHÔNG
   * tự ý coi hết lượt là fail.
   */
  async function handleRecheck() {
    if (!importId) return;
    setRechecking(true);
    try {
      const job = await orderImportService.getStatus(importId);
      setResult(job);
      if (job.status === "completed") {
        onImported();
        setPollExhausted(false);
      } else if (job.status === "failed") {
        setPollExhausted(false);
      }
    } catch (error) {
      message.error(
        getApiErrorMessage(error, "Không kiểm tra được trạng thái phiên nhập."),
      );
    } finally {
      setRechecking(false);
    }
  }

  // Hết lượt poll (~2 phút) mà job vẫn `pending`/`processing`: KHÔNG được coi là thành công —
  // con số success_orders/error_rows/warning_rows lúc này là 0 do job chưa chạy xong, không
  // phải kết quả thật. Hiện thông báo trung tính, không hiện con số nào.
  const isStillProcessing =
    result !== null &&
    result.status !== "completed" &&
    result.status !== "failed";

  /**
   * Phiên nhập đã chạy XONG (dù thành công hay thất bại) ⇒ chân modal chỉ còn
   * nút "Đóng". Hết 2 phút poll mà job chưa xong thì KHÔNG tính là xong: lúc đó
   * `submitting` đã tắt nên nút hiện lại, và người dùng vẫn còn nút "Kiểm tra
   * lại" ở khối cảnh báo.
   */
  const hasFinished =
    result !== null &&
    (result.status === "completed" || result.status === "failed");

  return (
    <Modal
      title="Tải lên đơn hàng"
      open={open}
      onCancel={onClose}
      width={900}
      // Trong lúc job chạy: KHÔNG có nút nào. Đóng giữa chừng thì job vẫn chạy
      // tiếp ở nền nhưng người dùng mất bảng kết quả và không biết đơn nào đã
      // vào; bấm lại "Tạo đơn hàng" thì nạp lại đúng file đó lần nữa và sinh
      // đơn trùng. Xong rồi mới hiện "Đóng" — không hiện lại nút tạo, vì phiên
      // này đã chạy xong, muốn nạp file khác thì mở lại modal.
      footer={
        submitting
          ? null
          : hasFinished
            ? [
                <Button key="close" onClick={onClose}>
                  Đóng
                </Button>,
              ]
            : [
                <Button key="close" onClick={onClose}>
                  Đóng
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  // I4: chưa resolve `pos_sales_modes` thì không cho tạo đơn.
                  disabled={!isConfigResolved}
                  onClick={handleSubmit}
                >
                  Tạo đơn hàng
                </Button>,
              ]
      }
      // Ẩn luôn dấu X và chặn bấm ra ngoài trong lúc chạy — ẩn mỗi nút ở chân
      // modal mà chừa X thì người dùng vẫn đóng được, thành ra vô nghĩa.
      closable={!submitting}
      maskClosable={!submitting}
      keyboard={!submitting}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* I4: nói rõ VÌ SAO nút tạo đơn đang khoá. */}
        {!isConfigResolved && (
          <Alert
            type={isConfigLoading ? "info" : "error"}
            showIcon
            message={
              isConfigLoading ? CONFIG_LOADING_MESSAGE : CONFIG_UNRESOLVED_ERROR
            }
          />
        )}

        {/* Đang chạy thì KHOÁ cả cụm cấu hình: job đã cầm bản sao tham số nên
            đổi lúc này không có tác dụng gì, nhưng vẫn để nhìn thấy được để
            người dùng đối chiếu mình đã chọn kho/hình thức nào. */}
        <Button
          icon={<DownloadOutlined />}
          block
          disabled={submitting}
          onClick={handleDownloadTemplate}
        >
          Tải file mẫu đơn hàng
        </Button>

        <Row align="middle" gutter={12}>
          <Col flex="150px">
            <Text strong>Loại đơn:</Text>
          </Col>
          <Col flex="auto">
            <Segmented
              block
              disabled={submitting}
              value={orderType}
              onChange={(value) => setOrderType(value as OrderImportOrderType)}
              options={ORDER_TYPE_OPTIONS}
            />
          </Col>
        </Row>

        <Row align="middle" gutter={12}>
          <Col flex="150px">
            <Text strong>
              Chọn kho trừ tồn<Text type="danger">*</Text>:
            </Text>
          </Col>
          <Col flex="auto">
            <Select
              style={{ width: "100%" }}
              disabled={submitting}
              placeholder="Chọn kho"
              value={warehouseId}
              onChange={setWarehouseId}
              loading={isLoadingWarehouses}
              options={warehouseOptions}
            />
          </Col>
        </Row>

        {showModePicker && (
          <Row align="middle" gutter={12}>
            <Col flex="150px">
              <Text strong>Hình thức bán:</Text>
            </Col>
            <Col flex="auto">
              <Segmented
                block
                disabled={submitting}
                value={fulfillmentType}
                onChange={(value) =>
                  setFulfillmentType(value as OrderImportFulfillmentType)
                }
                options={FULFILLMENT_TYPE_OPTIONS}
              />
            </Col>
          </Row>
        )}
        {!showModePicker && canPreorder && (
          <Row align="middle" gutter={12}>
            <Col flex="150px">
              <Text strong>Hình thức bán:</Text>
            </Col>
            <Col flex="auto">
              <Text>Đặt trước</Text>
            </Col>
          </Row>
        )}
        {fulfillmentType === "PREORDER" && (
          <Row align="middle" gutter={12}>
            <Col flex="150px">
              <Text strong>
                Số ngày giao dự kiến<Text type="danger">*</Text>:
              </Text>
            </Col>
            <Col flex="auto">
              <Space size="middle">
                <InputNumber
                  aria-label="Số ngày giao dự kiến"
                  disabled={submitting}
                  min={1}
                  precision={0}
                  style={{ width: 160 }}
                  value={deliveryDays}
                  onChange={(value) => setDeliveryDays(value)}
                />
                {deliveryDays ? (
                  <Text type="secondary">
                    Dự kiến giao:{" "}
                    {dayjs().add(deliveryDays, "day").format("DD/MM/YYYY")}
                  </Text>
                ) : null}
              </Space>
            </Col>
          </Row>
        )}

        <Text type="secondary">File có dung lượng tối đa 10MB</Text>

        <Upload
          accept=".xlsx,.xls"
          maxCount={1}
          disabled={submitting}
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: newFileList }) => setFileList(newFileList)}
        >
          <Button icon={<UploadOutlined />} disabled={submitting}>
            Chọn file
          </Button>
        </Upload>

        {/* TCR-1547: trong lúc poll (`submitting === true`), hiện tiến độ ngay thay vì
            spinner câm nín suốt tối đa 2 phút — người dùng cần biết job đang chạy hay
            đang kẹt. Task 9: kèm `OrderImportResultTable` với job mới nhất từ mỗi tick
            để bảng kết quả từng đơn chạy dần trong lúc job đang xử lý, thay vì chỉ có
            các Alert tiến độ này. */}
        {submitting && tick && (
          <>
            {tick.job.status === "pending" &&
              tick.elapsedMs < PENDING_STALL_MS && (
                <Alert
                  type="info"
                  showIcon
                  message={`Đang chờ hệ thống nhận xử lý… (${Math.round(
                    tick.elapsedMs / 1000,
                  )}s)`}
                />
              )}
            {/* Job kẹt ở "pending" quá PENDING_STALL_MS (chưa từng sang "processing")
                nghĩa là worker nền chưa nhặt job khỏi hàng đợi — khác với job đang chạy
                thật mà chậm (trường hợp đó status đã sang "processing" ở nhánh trên). */}
            {tick.job.status === "pending" &&
              tick.elapsedMs >= PENDING_STALL_MS && (
                <Alert
                  type="warning"
                  showIcon
                  message="Hệ thống chưa bắt đầu xử lý file"
                  description={`Đã ${Math.round(
                    tick.elapsedMs / 1000,
                  )} giây mà phiên nhập vẫn chưa được nhận xử lý — dịch vụ xử lý nền có thể đang bận hoặc gặp sự cố. Mã phiên: ${importId}`}
                />
              )}
            <OrderImportResultTable job={tick.job} />
          </>
        )}

        {result && (
          <>
            {result.status === "completed" && (
              <Alert type="success" message={summarize(result)} showIcon />
            )}
            {result.status === "failed" && (
              <Alert
                type="error"
                message={result.error_message ?? "Nhập đơn hàng thất bại."}
                showIcon
              />
            )}
            {isStillProcessing && pollExhausted && (
              <Alert
                type="warning"
                showIcon
                message="Chưa có kết quả sau 2 phút"
                description={`Đơn có thể vẫn đang được xử lý ở nền. Vui lòng KHÔNG tải lại file để tránh tạo trùng đơn. Mã phiên: ${importId}`}
                action={
                  <Button size="small" loading={rechecking} onClick={handleRecheck}>
                    Kiểm tra lại
                  </Button>
                }
              />
            )}
            <OrderImportResultTable job={result} />
          </>
        )}
      </Space>
    </Modal>
  );
}

export default ImportOrderModal;
